import { useState } from 'react';
import { FieldArray } from 'react-final-form-arrays';
import { Field, FormSpy } from 'react-final-form';
import cn from 'classnames';
import { connect } from 'react-redux';

import {
  IKnmErknmFormViolatedRequirementsMandatory,
  IKnmErknmFormViolatedRequirementsNpa,
} from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormViolatedRequirementsNpa';
import { SelectInputFieldAutoOptions } from 'UIPackage/MainModule/components/SelectInputFieldAutoOptions';
import { CommonCatalogsNamesEnum } from 'CorePackage/CatalogsModule/enums/CommonCatalogsNamesEnum';
import { ErknmActViolationItems } from 'KnmErknmPackage/ErknmViolationsModule/components/ErknmActViolationItems';
import { IDecisionFormRef } from 'DecisionPackage/MainModule/interfaces/IDecisionFormRef';
import { getUserRoleIsSupervisor } from 'AuthPackage/MainModule/selectors/authSelectors';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { getFieldArrayErrorMessage } from 'CorePackage/MainModule/common/getFieldArrayErrorMessage';
import {
  getDecisionFormRef,
  getKnmIsDecisionStatusFillInProcess,
} from 'DecisionPackage/MainModule/selectors/decisionSelectors';
import { StructureRequirementsNpaModal } from 'KnmPackage/StructureRequirementsNpaModule/StructureRequirementsNpaModal';
import { IRequirementNpa } from 'APIPackage/RequirementsNpaModule/interfaces/IRequirementNpa';
import { IRequirementStructuralUnit } from 'APIPackage/RequirementsNpaModule/interfaces/IRequirementStructuralUnit';
import { IRequirementsNpaInitValues } from 'KnmPackage/RequirementsNpaModalModule/components/RequirementsNpaModal';

import { Button } from '../../../UIPackage/MainModule/components/Button';
import { KnmCollapse } from '../../../KnmPackage/MainModule/components/KnmCollapse';
import styles from './DecisionViolations.module.scss';
import collapseStyles from '../../../KnmPackage/MainModule/components/KnmCollapse.module.scss';
import { IDictionaryKnoOrganizationSelectOption } from '../../../CorePackage/DictionariesModule/interfaces/IDictionaryKnoOrganizationSelectOption';
import { IDictionarySupervisionTypeSelectOption } from '../../../CorePackage/DictionariesModule/interfaces/IDictionarySupervisionTypeSelectOption';
import { IRequirementsNpaItem } from '../../../KnmPackage/RequirementsNpaModalModule/interfaces/IRequirementsNpaItem';
import { checkHasErrorInCollapseBlock } from '../../../functions/checkHasErrorInCollapseBlock';
import { IKnmErknmFormObject } from '../../../KnmErknmPackage/MainModule/interfaces/IKnmErknmFormObject';

interface IDecisionInitialViolationsProps {
  watchOnly: boolean;
  isViolationFieldsWatchOnly: boolean;
  knoOrganization: IDictionaryKnoOrganizationSelectOption | undefined;
  supervisionType: IDictionarySupervisionTypeSelectOption | undefined;
  objects?: IKnmErknmFormObject[];
  isRequirementsModalVisible: boolean;
  initValues: IRequirementsNpaInitValues | undefined;
  setIsRequirementsModalVisible: (val: boolean) => void;
  setInitValues: (val: IRequirementsNpaInitValues | undefined) => void;
}

interface IDecisionInitialViolationsStoreProps {
  formRef: IDecisionFormRef | undefined;
  isSupervisor: boolean;
  isDecisionStatusFillInProcess: boolean;
}

const DecisionInitialViolationsWithoutStore = ({
  watchOnly,
  isViolationFieldsWatchOnly,
  formRef,
  knoOrganization,
  supervisionType,
  objects,
  isDecisionStatusFillInProcess,
  isSupervisor,
  setIsRequirementsModalVisible,
  initValues,
  setInitValues,
  isRequirementsModalVisible,
}: IDecisionInitialViolationsProps & IDecisionInitialViolationsStoreProps) => {
  const [knmCollapseOpened, setKnmCollapseOpened] = useState<{ [key: string]: boolean }>({});
  const isDisableViolations = !isSupervisor || !isDecisionStatusFillInProcess;

  const requirementsFieldName = 'violatedMandatoryRequirements';
  const push = formRef?.current.mutators.push;
  const remove = formRef?.current.mutators.remove;
  const [npaFieldName, setNpaFieldName] = useState('');

  const handleAddRequirementsNpa = (npaItems: IRequirementsNpaItem[]) => {
    if (initValues && npaItems.length === 1) {
      push &&
        npaItems[0].mandatory?.forEach(item => {
          const newMandatoryItem: IKnmErknmFormViolatedRequirementsMandatory = {
            mandatory: item,
            category: undefined,
            violations: null,
          };

          push(`${npaFieldName}.violatedMandatory`, newMandatoryItem);
        });
    } else {
      push &&
        npaItems.forEach(item => {
          const newNpaItem: IKnmErknmFormViolatedRequirementsNpa = {
            guid: null,
            isNew: true,
            npa: item.npa,
            structuralUnit: item.structuralUnit,
            violatedMandatory: item.mandatory.map(mandatoryItem => ({
              mandatory: mandatoryItem,
              category: undefined,
              violations: null,
            })),
          };

          push(requirementsFieldName, newNpaItem);
        });
    }
  };

  const handleAddMandatory = (npa: IRequirementNpa, structuralUnit: IRequirementStructuralUnit, fieldName: string) => {
    setInitValues({
      npa,
      structuralUnit,
    });
    setNpaFieldName(fieldName);
    setIsRequirementsModalVisible(true);
  };

  return (
    <>
      <FormSpy subscription={{ errors: true, touched: true }}>
        {props => {
          const isFieldTouched = props.touched?.violatedMandatoryRequirements;
          const violatedRequirementsError = getFieldArrayErrorMessage(props.errors?.violatedMandatoryRequirements);

          return (
            <div className={styles.NpaListWrapper}>
              <FieldArray name={requirementsFieldName}>
                {({ fields }) =>
                  fields.map((name, index) => {
                    const fieldValue: IKnmErknmFormViolatedRequirementsNpa = fields.value?.[index];
                    const { npa, structuralUnit, isNew } = fieldValue;

                    if (!npa || !structuralUnit) return null;
                    const npaErrorField = props.errors?.violatedMandatoryRequirements?.[index];

                    const npaError = npaErrorField?.npa;
                    const mandatoryError = getFieldArrayErrorMessage(npaErrorField?.violatedMandatory);
                    const isMandatoryItemError =
                      Array.isArray(npaErrorField?.violatedMandatory) &&
                      !!npaErrorField.violatedMandatory.filter(
                        (m: IKnmErknmFormViolatedRequirementsMandatory) => !!m?.mandatory?.id,
                      ).length;

                    const hasError = checkHasErrorInCollapseBlock(formRef, name);

                    return (
                      <div key={name}>
                        <div
                          className={cn(styles.RequirementsNpaItem, {
                            [styles.RequirementsNpaItemError]:
                              isFieldTouched && (npaError || mandatoryError || isMandatoryItemError),
                          })}
                        >
                          <KnmCollapse
                            id={name}
                            theme="fields"
                            title={
                              <div className={styles.CollapseTitle}>
                                <span className={styles.CollapseTitleGrey}>НПА №</span>
                                {npa.number}
                                <span className={styles.CollapseTitleGrey}>от</span>
                                {npa.acceptanceDate}
                              </div>
                            }
                            onClose={
                              watchOnly || isDisableViolations
                                ? undefined
                                : () => remove && remove(requirementsFieldName, index)
                            }
                            openByDefault={isNew}
                            changeOpenedCallback={e =>
                              setKnmCollapseOpened(state => ({
                                ...state,
                                [name]: e,
                              }))
                            }
                            error={hasError && knmCollapseOpened[name]}
                          >
                            <>
                              <Field name={`${name}.npa`}>{() => null}</Field>
                              <div className={styles.NpaName}>{npa.title}</div>
                              <div className={styles.StructureUnitTitle}>
                                <span>Структурная единица</span>
                                <span>{structuralUnit.title}</span>
                              </div>
                              <div className={styles.BeforeMandatoryBlock}>
                                Нарушенные обязательные требования
                                {!watchOnly && !isDisableViolations && (
                                  <span className="shared-no-wrap-text">
                                    <Button
                                      className="shared-btn-add"
                                      text="Добавить"
                                      type="button"
                                      variant="link"
                                      color="primary"
                                      id="decisionPassportMandatoryAddButton"
                                      onClick={() => handleAddMandatory(npa, structuralUnit, name)}
                                    />
                                  </span>
                                )}
                              </div>
                              {isFieldTouched && mandatoryError && (
                                <p className="shared-input-error shared-row__small-margin">{mandatoryError}</p>
                              )}
                              <FieldArray name={`${name}.violatedMandatory`}>
                                {({ fields: mFields }) =>
                                  mFields.map((mName, mIndex) => {
                                    const mandatory: IKnmErknmFormViolatedRequirementsMandatory =
                                      mFields.value?.[mIndex];

                                    if (!mandatory) return null;
                                    const mandatoryItemError = npaErrorField?.violatedMandatory?.[mIndex]?.mandatory;

                                    return (
                                      <div className={styles.ViolatedMandatoryWrapper} key={mName}>
                                        <div
                                          className={cn(styles.ViolatedMandatoryBlock, {
                                            [styles.ViolatedMandatoryBlockError]: mandatoryItemError && isFieldTouched,
                                          })}
                                        >
                                          <Field name={`${mName}.mandatory.id`}>{() => null}</Field>
                                          <div className={styles.Mandatory}>
                                            <span className={styles.ViolatedMandatoryHeader}>
                                              Содержание требования
                                            </span>
                                            {mandatory.mandatory.title}
                                          </div>
                                          <div className={styles.Category}>
                                            <span className={styles.ViolatedMandatoryHeader}>
                                              Значимость последствий нарушения
                                            </span>
                                            <SelectInputFieldAutoOptions
                                              name={`${mName}.category`}
                                              optionName={CommonCatalogsNamesEnum.MANDATORY_CATEGORIES}
                                              disabled={watchOnly || isDisableViolations}
                                            />
                                          </div>
                                          {!watchOnly && !isDisableViolations && (
                                            <div>
                                              <button
                                                type="button"
                                                className={collapseStyles.DeleteButton}
                                                onClick={() => remove && remove(`${name}.violatedMandatory`, mIndex)}
                                              >
                                                <span className={collapseStyles.DeleteButtonIcon} />
                                              </button>
                                            </div>
                                          )}
                                        </div>
                                        <ErknmActViolationItems
                                          formRef={formRef}
                                          isAddEnabled={!(watchOnly || isDisableViolations)}
                                          watchOnly={isViolationFieldsWatchOnly}
                                          name={mName}
                                          mId={mName}
                                        />
                                        {isFieldTouched && typeof mandatoryItemError === 'string' && (
                                          <p className="shared-input-error shared-row__small-margin">
                                            {mandatoryItemError}
                                          </p>
                                        )}
                                      </div>
                                    );
                                  })
                                }
                              </FieldArray>
                            </>
                          </KnmCollapse>
                        </div>
                        {isFieldTouched && npaError && (
                          <p className="shared-input-error shared-row__small-margin">{npaError}</p>
                        )}
                      </div>
                    );
                  })
                }
              </FieldArray>
              {violatedRequirementsError && (
                <p className="shared-input-error shared-row__small-margin">{violatedRequirementsError}</p>
              )}
            </div>
          );
        }}
      </FormSpy>
      {isRequirementsModalVisible && (
        <StructureRequirementsNpaModal
          onClose={() => setIsRequirementsModalVisible(false)}
          knoVersionId={knoOrganization?.value.dictVersionId || ''}
          supervisionVersionId={supervisionType?.value.dictVersionId || ''}
          withRequirements={true}
          addRequirementsNpa={handleAddRequirementsNpa}
          initValues={initValues}
          objects={objects}
        />
      )}
    </>
  );
};

const mapStateToProps = (state: IRootState): IDecisionInitialViolationsStoreProps => ({
  formRef: getDecisionFormRef(state),
  isSupervisor: getUserRoleIsSupervisor(state),
  isDecisionStatusFillInProcess: getKnmIsDecisionStatusFillInProcess(state),
});

export const DecisionInitialViolations = connect(mapStateToProps)(DecisionInitialViolationsWithoutStore);
